import type { RefObject } from 'react';
import { useCallback, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { CloseBold, Search16 } from '@elfsight/icons';
import { getColorWithAlpha, isDarkColor } from '@elfsight/lib-utils';
import { flattenColor } from '@elfsight/widget-filters';
import { Icon, TextControlBase } from '@elfsight/widget-ui';
import { createReadableClass, getInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useTranslation } from 'react-i18next';
import { useSearch } from './SearchProvider';

type SearchProps = {
  maxWidth?: number;
  borderRadius?: number;
};

export function Search({ maxWidth, borderRadius }: SearchProps) {
  const { t } = useTranslation();
  const searchRef = useRef<{ inputRef?: RefObject<HTMLInputElement> }>(null);
  const { filtersAndSearchColor, activeFiltersColor } = useSettings();
  const [focused, setFocused] = useState(false);
  const { searchValue, reset, setSearchValue } = useSearch();

  const primaryColor = getColorWithAlpha(filtersAndSearchColor, 0.5);

  const handleFocus = useCallback(() => {
    if (!searchRef.current?.inputRef) {
      return;
    }
    searchRef.current.inputRef.current?.focus();
  }, []);

  return (
    <SearchContainer
      _focused={focused}
      _textColor={filtersAndSearchColor}
      _accentColor={activeFiltersColor}
      _width={maxWidth}
      _withBackground={!isDarkColor(filtersAndSearchColor)}
      _borderRadius={borderRadius}
      onClick={handleFocus}
    >
      <Slot $edge="left" style={{ opacity: 0.5 }}>
        <Icon component={Search16} size={16} />
      </Slot>

      <SearchInput
        ref={searchRef}
        value={searchValue}
        padding={0}
        borderWidth={0}
        fontSize={14}
        lineHeight="20px"
        placeholder={t('Search jobs')}
        color={filtersAndSearchColor}
        placeholderColor={primaryColor}
        onValueChange={(value) => setSearchValue(value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />

      {searchValue && (
        <Slot $edge="right">
          <ClearButton
            _textColor={filtersAndSearchColor}
            onClick={reset}
            style={{ cursor: 'pointer' }}
          >
            <Icon component={CloseBold} size={8} />
          </ClearButton>
        </Slot>
      )}
    </SearchContainer>
  );
}

const Slot = styled.div.attrs({
  className: createReadableClass({
    component: 'search',
    element: 'slot'
  })
})<{ $edge?: 'left' | 'right' }>`
  display: inline-flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;

  ${({ $edge }) =>
    $edge &&
    {
      left: css`
        margin-right: 8px;
        margin-left: -6px;
      `,
      right: css`
        margin-left: 4px;
        margin-right: -6px;
      `
    }[$edge]}
`;

const SearchInput = styled(TextControlBase).attrs({
  className: createReadableClass({
    component: 'search',
    element: 'input'
  })
})`
  flex: 1 1 auto;
`;

const SearchContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'search',
    element: 'container'
  })
})<{
  _focused: boolean;
  _textColor: string;
  _accentColor: string;
  _width?: number;
  _withBackground: boolean;
  _borderRadius?: number;
}>`
  position: relative;
  min-width: 0;
  cursor: text;
  padding: 7px 15px;
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  border-style: solid;
  border-width: 1px;
  transition: all 0.2s ease;
  border-radius: ${({ _borderRadius }) =>
    _borderRadius !== undefined ? `${_borderRadius}px` : '8px'};
  width: 100%;
  height: 36px;

  ${({ _width }) =>
    _width &&
    css`
      max-width: ${_width}px;
    `}

  ${({ _textColor, _withBackground }) => css`
    color: ${_textColor};
    border-color: ${getColorWithAlpha(_textColor, 0.3)};
    background-color: ${flattenColor(
      getColorWithAlpha(_textColor, 0),
      _withBackground ? getInvertedColor(_textColor) : undefined
    )};

    & svg {
      fill: ${_textColor};
    }
  `}

  ${({ _focused, _accentColor, _textColor, _withBackground }) => {
    if (_focused) {
      return css`
        border-color: ${_accentColor};
        box-shadow: 0 0 0 1px ${_accentColor};
      `;
    }

    return css`
      &[data-state='open'],
      &:hover {
        border-color: ${getColorWithAlpha(_textColor, 0.7)};
        background-color: ${flattenColor(
          getColorWithAlpha(_textColor, 0.02),
          _withBackground ? getInvertedColor(_textColor) : undefined
        )};
      }
    `;
  }}
`;

const ClearButton = styled.div.attrs({
  className: createReadableClass({
    component: 'search',
    element: 'clear-button'
  })
})<{ _textColor: string }>`
  padding: 4px;
  border-radius: 50%;
  background-color: ${({ _textColor }) => getColorWithAlpha(_textColor, 0.1)};

  & svg {
    fill: ${({ _textColor }) => getColorWithAlpha(_textColor, 0.7)};
  }

  &:hover svg {
    fill: ${({ _textColor }) => _textColor};
  }
`;
